import { sortBy as _sortBy } from 'lodash'
import { addDays, differenceInMinutes, endOfDay, isAfter, subDays } from 'date-fns'

import Api from 'services/Api'
import { Format, Parse } from 'services/Dates'
import { store } from 'store/reducers'
import { createErrorNotification, createNotification } from 'store/actions/notifications'
import { hasAccessToThis } from 'services/Utilities'
import { ScheduleEvent } from 'Api'

// Converts a time object to a dateTime with a specific date
export const getDateTime = (date: Date | string, time?: Date | string, standardTimeDateDisplay?: boolean): Date => {
  const datePrefix = date instanceof Date ? Format(date, { outFormat: 'date' }) : date
  const timeSuffix = time instanceof Date ? Format(time, { outFormat: 'time' }) : time
  if (standardTimeDateDisplay) {
    const parsed = Parse(`${datePrefix} ${timeSuffix}`, { inFormat: 'dateTime', forceStandardTime: true })
    return parsed
  }
  return Parse(`${datePrefix}T${timeSuffix}:00`)
}

// Converts a time value to a dateTime that is between arrival and departure
export const getRelativeTimeToDates = ({
  start,
  end,
  time,
  standardTimeDateDisplay
}: {
  start: Date
  end?: Date
  time: Date
  standardTimeDateDisplay: boolean
}): Date => {
  let dateTime = getDateTime(start, time, standardTimeDateDisplay)
  if (end && (dateTime < start || dateTime > end)) dateTime = getDateTime(end, time)
  return dateTime
}

export const getArrivalLeaveFromReference = ({
  arrivalTime,
  leaveTime,
  referenceDate,
  end,
  standardTimeDateDisplay
}: {
  arrivalTime: Date | string
  leaveTime: Date | string
  referenceDate: Date | string
  end: Date | string
  standardTimeDateDisplay?: boolean
}): { arrival: Date; leave: Date } => {
  // Get the arrival and the leave time objects
  let arrival
  let leave

  if (referenceDate) {
    if (arrivalTime) {
      arrival = getDateTime(referenceDate, arrivalTime, standardTimeDateDisplay)
    }

    if (leaveTime && arrivalTime) {
      leave = getDateTime(referenceDate, leaveTime, standardTimeDateDisplay)
      const isOvernight = leave < arrival
      /**
       * If the leave is after the arrival, assume that the shift is an overnight shift
       */
      if (isOvernight) leave = addDays(leave, 1)

      /**
       * If the arrival is after the end of the shift, this is probably
       * a use-case where the user is punching on the day before a shift that
       * starts in the very early morning.
       * */
      if (end && arrival > Parse(end) && isOvernight) {
        arrival = subDays(arrival, 1)
        leave = subDays(leave, 1)
      }
    }
  }
  return { arrival, leave }
}

// Checks if a specified time is contained within a punch card
export const containedInPunchCard = (
  {
    arrivalTime,
    leaveTime,
    referenceDate,
    end,
    standardTimeDateDisplay
  }: { arrivalTime: string | Date; leaveTime: string | Date; standardTimeDateDisplay: boolean; referenceDate: string | Date; end: string },
  rawTime: Date
): boolean => {
  if (!arrivalTime || !leaveTime || !referenceDate) return true
  const { arrival, leave } = getArrivalLeaveFromReference({
    arrivalTime: arrivalTime,
    leaveTime: leaveTime,
    referenceDate: referenceDate,
    end,
    standardTimeDateDisplay
  })

  const time = getRelativeTimeToDates({ start: arrival, end: leave, time: rawTime, standardTimeDateDisplay })
  if (!time) return false
  return time <= leave && time >= arrival
}

export const getPunchCards = async ({ filter, sortBy }) => {
  const { result, success, dateRangeExceeded } = await Api(`/punch/reports/hours`, { params: { ...filter, sort: sortBy } })
  if (!success) return store.dispatch(createErrorNotification())
  if (dateRangeExceeded)
    store.dispatch(
      createNotification({
        title: 'manager.punch.DATE_RANGE_EXCEEDED.TITLE',
        description: 'manager.punch.DATE_RANGE_EXCEEDED.DESCRIPTION',
        type: 'warning'
      })
    )

  const { rangeStart, rangeEnd } = filter
  const range = rangeStart && rangeEnd ? `${rangeStart}_${rangeEnd}` : ''
  return [result, range]
}

export const checkCanApprovePunchCard = (isOwner: boolean): boolean => {
  const hasAccessToApproveOwn = hasAccessToThis('punchCardApproveOwn')
  if (hasAccessToApproveOwn) return true

  return !isOwner
}

export const PUNCH_INTERVAL_IN_MINUTES = 720

export const canEmployeePunch = (event: Partial<MData.Event> | ScheduleEvent): { allowedToPunchIn: boolean; allowedToPunchOut: boolean } => {
  const shiftStartDate = event?.start && Parse(event.start)
  const shiftEndDate = event?.end && Parse(event.end)

  const differenceInMinutesFromStart = shiftStartDate ? differenceInMinutes(Parse(new Date()), shiftStartDate) : null
  const differenceInMinutesFromEnd = shiftEndDate ? differenceInMinutes(Parse(new Date()), shiftEndDate) : null

  const isDifferenceInMinutesWithinInterval = (diff: number | null, hourRule: number) => {
    if (diff === 0) return true
    return diff ? diff > -hourRule && diff < hourRule : false
  }

  return {
    allowedToPunchIn: isDifferenceInMinutesWithinInterval(differenceInMinutesFromStart, PUNCH_INTERVAL_IN_MINUTES),
    allowedToPunchOut: isDifferenceInMinutesWithinInterval(differenceInMinutesFromEnd, PUNCH_INTERVAL_IN_MINUTES)
  }
}

export const isPunchCardLocked = (punchCardLockDate: string, referenceDate?: string | Date) => {
  return Boolean(referenceDate && punchCardLockDate && isAfter(endOfDay(Parse(punchCardLockDate)), Parse(referenceDate)))
}
